import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { Section, TextField } from './FormFields';
import RegionSettingsPanel from './leads/RegionSettingsPanel';
import VoucherCodesPanel from './leads/VoucherCodesPanel';
import RegionHealthPanel from './leads/RegionHealthPanel';
import WeeklyInboundDigest from './leads/WeeklyInboundDigest';
import ManagerDigestSubscription from './leads/ManagerDigestSubscription';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadsTab = ({ content, updateLeadRouting, role = 'admin', onFixRegionOwner }) => {
  const [languages, setLanguages] = useState([]);
  const [regionRefreshSignal, setRegionRefreshSignal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/languages`);
        if (!cancelled) setLanguages(res.data.languages || []);
      } catch (err) {
        if (!cancelled) setLanguages([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const routes = content.leadRouting?.routes || {};

  return (
    <div className="space-y-6">
      <Section
        title="Lead Routing"
        description="Configure which email address receives contact form submissions for each language. Add languages under the Languages tab to see them appear here."
      >
        <div className="space-y-4">
          {languages.length === 0 && (
            <p className="text-sm text-slate-400 italic">Loading languages…</p>
          )}
          {languages.map((lang) => {
            // Routes are keyed by the language English name lowercased (e.g. "japanese").
            // The backend router accepts either the name or the code, but we key by name
            // here to match legacy values already stored on production.
            const key = (lang.name || lang.code || '').toLowerCase();
            const nativeName = lang.nativeName && lang.nativeName !== lang.name
              ? ` (${lang.nativeName})`
              : '';
            return (
              <TextField
                key={key}
                label={`${lang.name} Leads${nativeName}`}
                value={routes[key] || ''}
                onChange={(v) => updateLeadRouting(key, v)}
                placeholder={`${key}@englishpassion.uk`}
                testId={`lead-routing-${lang.code}`}
              />
            );
          })}
          <TextField
            label="Default (No language selected)"
            value={routes.default || ''}
            onChange={(v) => updateLeadRouting('default', v)}
            placeholder="contact@englishpassion.uk"
            testId="lead-routing-default"
          />
        </div>
      </Section>
      <RegionHealthPanel role={role} onFixRegionOwner={onFixRegionOwner} refreshSignal={regionRefreshSignal} />
      {role === 'admin' && <WeeklyInboundDigest />}
      {role === 'manager' && <ManagerDigestSubscription />}
      <RegionSettingsPanel role={role} onOwnerChanged={() => setRegionRefreshSignal((k) => k + 1)} />
      {role === 'admin' && <VoucherCodesPanel />}
    </div>
  );
};

const AnalyticsTab = ({ content, updateAnalytics }) => {
  return (
    <div className="space-y-6">
      <Section title="Analytics Integration" description="Configure tracking services for your website.">
        <div className="space-y-5">
          <TextField 
            label="Google Analytics 4 Measurement ID" 
            value={content.analytics?.ga4Id || ''} 
            onChange={(v) => updateAnalytics('ga4Id', v)} 
            placeholder="G-XXXXXXXXXX" 
          />
          <TextField 
            label="Microsoft Clarity Project ID" 
            value={content.analytics?.clarityId || ''} 
            onChange={(v) => updateAnalytics('clarityId', v)} 
            placeholder="xxxxxxxxxx" 
          />
          <TextField 
            label="Google Search Console Verification" 
            value={content.analytics?.searchConsoleId || ''} 
            onChange={(v) => updateAnalytics('searchConsoleId', v)} 
            placeholder="google-site-verification=XXXXXXXXXX" 
          />
        </div>
      </Section>

      <Section title="Analytics Dashboard" description="View your website performance metrics.">
        <div className="bg-slate-50 rounded-lg p-6 text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium mb-2">Analytics Dashboard</p>
          <Link 
            to="/admin/analytics" 
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2 rounded-lg font-medium hover:bg-slate-800"
          >
            <BarChart3 className="w-4 h-4" /> Open Dashboard
          </Link>
        </div>
      </Section>
    </div>
  );
};

export { LeadsTab, AnalyticsTab };
